import { Link } from 'react-router-dom';
import {
  PRIMARY,
  BG_DARKER,
  BORDER,
  TEXT,
  TEXT_DIM,
  TEXT_FAINT,
} from '../theme/publicTokens';

/**
 * Footer compartido entre todas las páginas públicas (Landing, Comparativa,
 * Methodology). Tres niveles:
 *   1. Navegación (links al resto del sitio público)
 *   2. Disclaimer regulatorio (importante para SaMD framework)
 *   3. Créditos + versión
 *
 * Nota regulatoria: el disclaimer es deliberadamente conservador para
 * proteger contra interpretación incorrecta del producto como dispositivo
 * médico aprobado. Coordinar con regulatory expert antes de modificar.
 *
 * El disclaimer en inglés mantiene el mismo nivel de cautela que el español
 * — traducción no-promotional, sin softening del lenguaje conservador.
 *
 * Update v1.1 (22-may-2026): texto del disclaimer ampliado al alcance del
 * Marco Operativo Pre-Comercial ECG.WATCH v1.0 (ECGW-MARCO-PRECOM-v1.0).
 * Se agrega link a /aviso-regulatorio (ES) / /en/regulatory-notice (EN)
 * con la síntesis pública completa.
 */

type Lang = 'es' | 'en';

const STRINGS: Record<Lang, {
  comparativa: string;
  metodologia: string;
  privacidad: string;
  consentimiento: string;
  cta: string;
  disclaimer: string;
  avisoLink: string;
}> = {
  es: {
    comparativa: 'Comparativa',
    metodologia: 'Metodología',
    privacidad: 'Privacidad',
    consentimiento: 'Consentimiento',
    cta: 'Sumarme →',
    disclaimer:
      'ECG.WATCH es una plataforma de software en desarrollo. No es un ' +
      'dispositivo médico registrado ante ANMAT. No reemplaza la consulta ' +
      'médica ni sirve para emergencias — en caso de urgencia llamá al 107.',
    avisoLink: 'Aviso regulatorio →',
  },
  en: {
    comparativa: 'Comparison',
    metodologia: 'Methodology',
    privacidad: 'Privacy',
    consentimiento: 'Consent',
    cta: 'Join →',
    disclaimer:
      'ECG.WATCH is a software platform under development. It is not a ' +
      'medical device registered with Argentina\'s ANMAT. It does not ' +
      'replace medical consultation nor is it suitable for emergencies.',
    avisoLink: 'Regulatory notice →',
  },
};

export function PublicFooter({ lang = 'es' }: { lang?: Lang } = {}) {
  const t = STRINGS[lang];
  // El link al CTA va a la home del idioma actual.
  const ctaHref = lang === 'es' ? '/#lista-de-espera' : '/en#lista-de-espera';
  // El link al aviso regulatorio completo respeta el idioma activo.
  const avisoHref = lang === 'es' ? '/aviso-regulatorio' : '/en/regulatory-notice';

  return (
    <footer
      style={{
        background: BG_DARKER,
        borderTop: `1px solid ${BORDER}`,
        marginTop: 0,
      }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">

        {/* Nivel 1 — Navegación */}
        <div
          style={{
            padding: '40px 0 32px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 24,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span
            style={{
              fontFamily: "'Geist', sans-serif",
              fontWeight: 400,
              fontSize: 14,
              letterSpacing: '0.10em',
              color: TEXT,
              textTransform: 'uppercase',
            }}
          >
            ECG<span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: '#22D3EE', boxShadow: '0 0 10px rgba(34,211,238,0.9)', margin: '0 3px', verticalAlign: 'middle' }} aria-hidden="true" />WATCH
          </span>
          <nav
            className="font-mono text-xs uppercase tracking-wider"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 24,
              color: TEXT_DIM,
            }}
          >
            <Link to="/comparativa" style={{ color: 'inherit', textDecoration: 'none' }}>
              {t.comparativa}
            </Link>
            <Link to="/methodology" style={{ color: 'inherit', textDecoration: 'none' }}>
              {t.metodologia}
            </Link>
            <Link to="/privacidad" style={{ color: 'inherit', textDecoration: 'none' }}>
              {t.privacidad}
            </Link>
            <Link to="/consentimiento" style={{ color: 'inherit', textDecoration: 'none' }}>
              {t.consentimiento}
            </Link>
            <a
              href={ctaHref}
              style={{ color: PRIMARY, textDecoration: 'none' }}
            >
              {t.cta}
            </a>
          </nav>
        </div>

        {/* Nivel 2 — Disclaimer regulatorio + link a aviso completo */}
        <div
          style={{
            borderTop: `1px solid ${BORDER}`,
            padding: '28px 0',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 24,
              flexWrap: 'wrap',
            }}
          >
            <p
              style={{
                fontSize: 13,
                lineHeight: 1.6,
                color: TEXT_DIM,
                maxWidth: '60ch',
                margin: 0,
              }}
            >
              {t.disclaimer}
            </p>
            <Link
              to={avisoHref}
              className="font-mono text-xs uppercase tracking-wider"
              style={{
                color: PRIMARY,
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {t.avisoLink}
            </Link>
          </div>
        </div>

        {/* Nivel 3 — Créditos + versión */}
        <div
          style={{
            borderTop: `1px solid ${BORDER}`,
            padding: '20px 0 28px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 16,
            fontSize: 12,
            color: TEXT_FAINT,
          }}
          className="font-mono"
        >
          <span>
            <span
              style={{
                fontFamily: "'Geist', sans-serif",
                fontWeight: 400,
                letterSpacing: '0.10em',
                textTransform: 'uppercase',
              }}
            >
              ECG<span style={{ display: 'inline-block', width: 5, height: 5, borderRadius: '50%', background: '#22D3EE', boxShadow: '0 0 8px rgba(34,211,238,0.9)', margin: '0 2px', verticalAlign: 'middle' }} aria-hidden="true" />WATCH
            </span>
            <span> · © 2026</span>
          </span>
          <span>v1.0</span>
        </div>

      </div>
    </footer>
  );
}
